import styles from './top-tracks.module.css'

import { LuGripVertical } from '@react-icons/all-files/lu/LuGripVertical'
import { LuTrash2 } from '@react-icons/all-files/lu/LuTrash2'
import React from 'react'

import { TRACK } from '@disco/constants/ItemTypes'
import { useTranslation } from '@disco/i18n/use-translation'

import type { Track, TrackId } from 'types/track'

import Waveform from 'components/common/player/waveform'
import TrackInfo from 'components/common/track-info'
import { Box, Flex, IconButton, Text, Tooltip } from 'components/ds'
import Draggable from 'components/ui/draggable'
import { HoverProvider } from 'components/ui/hover-provider'

type Props = {
  track: Track
  index: number
  disabled: boolean
  onRemove: (trackId: TrackId) => void
}

/**
 * Draggable row in the top tracks list, with its position and a remove button.
 * @param track - Top track to show
 * @param index - Zero-based position in the list
 * @param disabled - Whether dragging and removing are disabled
 * @param onRemove - Called with the track's ID when it's removed
 */
const TopTrackRow = ({ track, index, disabled, onRemove }: Props) => {
  const { t } = useTranslation('catalogs-management')

  return (
    <Draggable id={track.id} type={TRACK} disabled={disabled}>
      <HoverProvider>
        <Flex align="center" gap="3" py="2" className={styles.trackRow}>
          <LuGripVertical className={styles.dragHandle} aria-hidden />
          <Text size="2" color="gray" className={styles.index}>
            {(index + 1).toString().padStart(2, '0')}
          </Text>
          <Box flexGrow="1" flexBasis="0" minWidth="0">
            <TrackInfo location="explorer" track={track} />
          </Box>
          <Waveform
            className={styles.waveform}
            track={track}
            barWidth={1.12}
            barGap={2.18 - 1.12}
            noAnimation
          />
          <Tooltip
            content={t(
              'catalogs-management:removeFromTopTracks',
              'Remove from top tracks',
            )}
          >
            <IconButton
              variant="ghost"
              color="gray"
              aria-label={t(
                'catalogs-management:removeTrackName',
                'Remove {{name}}',
                {
                  name: track.name,
                },
              )}
              disabled={disabled}
              onClick={() => onRemove(track.id)}
            >
              <LuTrash2 />
            </IconButton>
          </Tooltip>
        </Flex>
      </HoverProvider>
    </Draggable>
  )
}

export default TopTrackRow
